import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { SignOutButton } from '../app/SignOutButton';
import { useSession } from '../app/WorkforceContext';
import { navHubs, tataLetakBawah } from '../lib/nav-hub';
import { visibleNavItems } from '../lib/navigation';
import { isOversightRole, type WorkforceRole } from '../lib/worker-roles';

/**
 * Menu penuh — rumah bagi layar yang TIDAK muat di bilah bawah.
 *
 * ADA KARENA bilah bawah hanya punya lima slot sementara Workforce punya sebelas layar. Tanpa
 * halaman ini, menu yang tidak muat akan lenyap: pemakainya tidak punya cara tahu bahwa layar itu
 * ada, apalagi cara membukanya.
 *
 * PEMBALIKAN KEPUTUSAN (arah owner 22 Agu 2026). Sebelumnya halaman ini menampilkan SELURUH menu,
 * termasuk yang sudah ada di bilah bawah, masing-masing bertanda "di bilah bawah". Alasan lama:
 * daftar yang berubah isi mengikuti lebar layar membingungkan orang yang sedang mencari sesuatu.
 *
 * Alasan itu tidak bertahan pada bentuk aplikasi ini, dan owner benar membatalkannya: bilah bawah
 * Workforce SELALU terlihat, di setiap layar. Jadi menu yang ada di sana tidak pernah "hilang"
 * ketika dihapus dari daftar ini — ia justru sedang tampil, satu ketukan jauhnya, saat daftar ini
 * dibaca. Mengulanginya membuat halaman ini dua kali lebih panjang tanpa menambah satu tujuan pun
 * yang tidak bisa dicapai.
 *
 * Bentuk barisnya mengikuti menu CRM: satu blok bersudut per tujuan, teks redup, terisi tinta
 * saat aktif. Satu kosakata untuk dua produk.
 *
 * Keluar PINDAH KE SINI dari header (item 12, review 12 Sep 2026). Header sekarang satu baris
 * identitas (nama org · nama pemakai · peran, lihat `AppShell`) yang tampil di SETIAP layar —
 * menaruh tindakan yang mahal dibatalkan di tempat itu berarti ia satu ketukan jauhnya dari mana
 * pun, termasuk dari layar yang sedang diisi kurir. Di sini ia satu-satunya tindakan di halaman
 * yang memang dibuka untuk mencari tindakan, bukan untuk bekerja.
 *
 * Tombol Keluar sendiri kini di `SignOutButton` (WKF-002, #1155, 14 Sep 2026) — halaman ini
 * bukan lagi satu-satunya rumahnya, karena rute ke sini (lewat `BottomNav`) hilang bagi
 * Owner/Manajer di desktop. `Sidebar` memakai komponen yang SAMA.
 */
export function MenuPage(): ReactNode {
  const session = useSession();
  const oversight = isOversightRole(session.me.roles as readonly WorkforceRole[]);
  const { overflow } = tataLetakBawah(navHubs(visibleNavItems(session.me.permissions), oversight));

  return (
    <>
      <section className="card">
        <h2>Lainnya</h2>
        {overflow.length === 0 ? (
          /*
           * Sejak #1758 halaman ini SELALU terjangkau (tombol Lainnya selalu ada, karena di sinilah
           * tombol Keluar), jadi keadaan kosong ini kini keadaan NORMAL bagi kurir — bukan lagi
           * keadaan yang hanya tercapai lewat alamat.
           */
          <p className="muted small">Semua menu Anda sudah ada di bilah bawah.</p>
        ) : (
          /*
           * Satu bagian per GRUP (#1758), judulnya nama grup, isinya layar di dalam grup itu —
           * langsung, tanpa harus membuka grupnya dulu lalu memilih tab.
           */
          overflow.map((hub) => (
            <div key={hub.key} className="menu-section">
              <h3 className="menu-section-label">{hub.label}</h3>
              <nav className="menu-list">
                {hub.members.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) => (isActive ? 'menu-item active' : 'menu-item')}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          ))
        )}
      </section>

      <section className="card">
        <SignOutButton />
      </section>
    </>
  );
}
